import React, { useState, useEffect } from 'react';
import { libraryService } from '../../services/index.js';
import { Book, User, Hash, Tag, BookOpen, Clock, X, Calendar, Search, RefreshCw, CheckCircle, AlertTriangle, Layers, DollarSign, ArrowRight } from 'lucide-react';

const LibraryManagement = ({
  students = [],
  teachers = [],
  showSuccessNotification,
  showErrorNotification,
  addActivity,
  externalActiveTab,
  setExternalActiveTab
}) => {
  // Navigation Tabs: 'inventory', 'register', 'issue', 'return', 'transactions', 'overdue'
  const [internalActiveTab, setInternalActiveTab] = useState('inventory');
  const activeTab = externalActiveTab || internalActiveTab;
  const setActiveTab = setExternalActiveTab || setInternalActiveTab;

  // Data States
  const [libraryBooks, setLibraryBooks] = useState([]);
  const [libraryIssues, setLibraryIssues] = useState([]);
  const [librarySettings, setLibrarySettings] = useState(null);
  const [libraryStats, setLibraryStats] = useState({
    totalBooks: 0,
    totalCopies: 0,
    availableCopies: 0,
    issuedCopies: 0,
    overdueCopies: 0
  });

  // Physical Copies Modal State
  const [selectedBookForCopies, setSelectedBookForCopies] = useState(null);
  const [bookCopies, setBookCopies] = useState([]);
  const [loadingCopies, setLoadingCopies] = useState(false);

  // Search & Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [transactionFilter, setTransactionFilter] = useState('ALL');

  // Form States
  const [bookForm, setBookForm] = useState({
    title: '',
    author: '',
    subject: '',
    isbn: '',
    publisher: '',
    totalCopies: 1,
    rackLocation: ''
  });

  // Issue Form States (2-Step Fast Workflow)
  const [issueStep, setIssueStep] = useState(1);
  const [issueBorrowerQuery, setIssueBorrowerQuery] = useState('');
  const [selectedBorrower, setSelectedBorrower] = useState(null);
  const [issueAccessionQuery, setIssueAccessionQuery] = useState('');
  const [selectedCopyData, setSelectedCopyData] = useState(null);
  const [issueSubmitting, setIssueSubmitting] = useState(false);

  // Return Form States (1-Step Fast Scan Workflow)
  const [returnAccessionQuery, setReturnAccessionQuery] = useState('');
  const [activeReturnCopy, setActiveReturnCopy] = useState(null);
  const [returnCondition, setReturnCondition] = useState('GOOD');
  const [returnSubmitting, setReturnSubmitting] = useState(false);

  // Fine Payment Modal State
  const [payingFineIssue, setPayingFineIssue] = useState(null);
  const [finePayAmount, setFinePayAmount] = useState('');

  // Initial Data Load
  const loadData = async () => {
    try {
      const [booksRes, issuesRes, statsRes, settingsRes] = await Promise.all([
        libraryService.listBooks(),
        libraryService.listIssues(),
        libraryService.getLibraryStats(),
        libraryService.getLibrarySettings()
      ]);

      const booksList = Array.isArray(booksRes) ? booksRes : [];
      const issuesList = Array.isArray(issuesRes) ? issuesRes : [];

      setLibraryBooks(booksList);
      setLibraryIssues(issuesList);
      setLibrarySettings(settingsRes);

      if (statsRes) {
        setLibraryStats(statsRes);
      }
    } catch (err) {
      console.error('Failed to load library management data:', err);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open Copies Inspection Modal
  const handleInspectCopies = async (book) => {
    setSelectedBookForCopies(book);
    setLoadingCopies(true);
    try {
      const copies = await libraryService.getCopiesForBook(book.id);
      setBookCopies(copies);
    } catch (err) {
      console.error('Failed to load physical copies:', err);
    } finally {
      setLoadingCopies(false);
    }
  };

  // Register New Book Handler
  const handleSaveBook = async (e) => {
    if (e) e.preventDefault();
    if (!bookForm.title || !bookForm.author || !bookForm.subject) {
      if (showErrorNotification) showErrorNotification('Validation Error', 'Title, Author, and Category/Subject are required');
      return;
    }

    try {
      const payload = {
        title: bookForm.title,
        author: bookForm.author,
        subject: bookForm.subject,
        isbn: bookForm.isbn || '',
        publisher: bookForm.publisher || '',
        totalCopies: parseInt(bookForm.totalCopies || 1, 10),
        rackLocation: bookForm.rackLocation || ''
      };

      const res = await libraryService.createBook(payload);
      if (res && res.success) {
        if (showSuccessNotification) showSuccessNotification('Book Created', `"${payload.title}" registered with ${payload.totalCopies} physical copy records.`);
        if (addActivity) addActivity('student_added', 'Book Created', `"${payload.title}" added to catalogue`);
        setBookForm({ title: '', author: '', subject: '', isbn: '', publisher: '', totalCopies: 1, rackLocation: '' });
        setActiveTab('inventory');
        loadData();
      } else {
        if (showErrorNotification) showErrorNotification('Creation Failed', res?.error || 'Could not register book.');
      }
    } catch (err) {
      console.error('Failed to save book:', err);
    }
  };

  // Fast Issue - Lookup Accession Copy
  const handleLookupIssueCopy = async (accessionNo) => {
    if (!accessionNo || !accessionNo.trim()) return;
    try {
      const res = await libraryService.getCopyByAccession(accessionNo.trim());
      if (res && res.success) {
        setSelectedCopyData(res.data);
      } else {
        setSelectedCopyData(null);
        if (showErrorNotification) showErrorNotification('Copy Not Found', res?.error || 'No copy found with accession ID.');
      }
    } catch (err) {
      setSelectedCopyData(null);
    }
  };

  // Submit Issue Book
  const handleConfirmIssue = async () => {
    if (!selectedBorrower || !selectedCopyData || !selectedCopyData.copy) return;
    setIssueSubmitting(true);
    try {
      const payload = {
        borrowerId: selectedBorrower.id,
        borrowerName: selectedBorrower.name,
        borrowerType: selectedBorrower.type,
        borrowerClass: selectedBorrower.class || '',
        borrowerContact: selectedBorrower.phone || '',
        accessionNo: selectedCopyData.copy.accessionNo
      };

      const res = await libraryService.issueBook(payload);
      if (res && res.success) {
        if (showSuccessNotification) showSuccessNotification('Book Issued', res.message || 'Book issued successfully');
        if (addActivity) addActivity('payment_processed', 'Book Issued', `Copy ${selectedCopyData.copy.accessionNo} issued to ${selectedBorrower.name}`);
        // Reset issue state
        setSelectedBorrower(null);
        setSelectedCopyData(null);
        setIssueBorrowerQuery('');
        setIssueAccessionQuery('');
        setIssueStep(1);
        setActiveTab('inventory');
        loadData();
      } else {
        if (showErrorNotification) showErrorNotification('Issue Error', res?.error || 'Failed to issue book');
      }
    } catch (err) {
      if (showErrorNotification) showErrorNotification('Issue Error', 'Failed to complete book issue');
    } finally {
      setIssueSubmitting(false);
    }
  };

  // Lookup Return Copy
  const handleLookupReturnCopy = async (accessionNo) => {
    if (!accessionNo || !accessionNo.trim()) return;
    try {
      const res = await libraryService.getCopyByAccession(accessionNo.trim());
      if (res && res.success && res.data.activeIssue) {
        setActiveReturnCopy(res.data);
      } else {
        setActiveReturnCopy(null);
        if (showErrorNotification) showErrorNotification('Active Loan Not Found', res?.error || 'No active loan found for this accession ID.');
      }
    } catch (err) {
      setActiveReturnCopy(null);
    }
  };

  // Confirm Return Book
  const handleConfirmReturn = async () => {
    if (!activeReturnCopy || !activeReturnCopy.activeIssue) return;
    setReturnSubmitting(true);
    try {
      const issueId = activeReturnCopy.activeIssue.id;
      const res = await libraryService.returnBook(issueId, returnCondition);
      if (res && res.success) {
        if (showSuccessNotification) showSuccessNotification('Book Returned', res.message || 'Book returned successfully');
        if (addActivity) addActivity('student_updated', 'Book Returned', `Accession ${activeReturnCopy.activeIssue.accessionNo} returned by ${activeReturnCopy.activeIssue.borrowerName}`);
        setReturnAccessionQuery('');
        setActiveReturnCopy(null);
        setReturnCondition('GOOD');
        setActiveTab('inventory');
        loadData();
      } else {
        if (showErrorNotification) showErrorNotification('Return Error', res?.error || 'Failed to return book');
      }
    } catch (err) {
      if (showErrorNotification) showErrorNotification('Return Error', 'Failed to complete book return');
    } finally {
      setReturnSubmitting(false);
    }
  };

  // Renew Book Handler
  const handleRenewBook = async (issueId) => {
    try {
      const res = await libraryService.renewBook(issueId);
      if (res && res.success) {
        if (showSuccessNotification) showSuccessNotification('Book Renewed', res.message);
        loadData();
      } else {
        if (showErrorNotification) showErrorNotification('Renewal Error', res?.error || 'Failed to renew book');
      }
    } catch (err) {
      if (showErrorNotification) showErrorNotification('Renewal Error', 'Failed to renew book');
    }
  };

  // Pay Fine Handler
  const handlePayFineSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!payingFineIssue || !finePayAmount) return;
    try {
      const res = await libraryService.payFine(payingFineIssue.id, parseFloat(finePayAmount));
      if (res && res.success) {
        if (showSuccessNotification) showSuccessNotification('Payment Recorded', res.message);
        setPayingFineIssue(null);
        setFinePayAmount('');
        loadData();
      } else {
        if (showErrorNotification) showErrorNotification('Payment Error', res?.error || 'Failed to record payment');
      }
    } catch (err) {
      if (showErrorNotification) showErrorNotification('Payment Error', 'Failed to record payment');
    }
  };

  // All Potential Borrowers (Students + Teachers)
  const matchingBorrowers = () => {
    if (!issueBorrowerQuery || issueBorrowerQuery.trim().length < 2) return [];
    const query = issueBorrowerQuery.toLowerCase();
    
    const matchedStudents = students.filter(s => 
      (s.name || '').toLowerCase().includes(query) || 
      String(s.studentId || '').toLowerCase().includes(query)
    ).map(s => ({
      id: String(s.studentId),
      name: s.name,
      type: 'STUDENT',
      class: s.className || s.department || 'N/A',
      phone: s.phone || s.contactNumber || ''
    }));

    const matchedTeachers = teachers.filter(t => 
      (t.teacherName || '').toLowerCase().includes(query) || 
      String(t.teacherId || '').toLowerCase().includes(query)
    ).map(t => ({
      id: String(t.teacherId),
      name: t.teacherName,
      type: 'TEACHER',
      class: t.departmentName || 'Faculty',
      phone: t.phone || ''
    }));

    return [...matchedStudents, ...matchedTeachers].slice(0, 8);
  };

  // Filtered Books Inventory
  const filteredBooks = libraryBooks.filter(book => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !searchQuery || 
      (book.title || '').toLowerCase().includes(q) ||
      (book.author || '').toLowerCase().includes(q) ||
      (book.isbn || '').toLowerCase().includes(q);
    const matchesCategory = !categoryFilter || book.subject === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Filtered Transactions
  const filteredTransactions = libraryIssues.filter(tx => {
    if (transactionFilter === 'ALL') return true;
    return (tx.status || '').toUpperCase() === transactionFilter;
  });

  // Dynamic Overdue List
  const overdueList = libraryIssues.filter(tx => {
    if (tx.returnDate) return false;
    try {
      return new Date(tx.dueDate) < new Date();
    } catch (e) {
      return false;
    }
  });

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 50%, #f5f3ff 100%)',
      padding: '28px',
      margin: '-24px -28px',
      fontFamily: '"Plus Jakarta Sans", "Inter", "Outfit", sans-serif'
    }}>

      {/* HEADER SECTION */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <span style={{ color: '#4f46e5', fontWeight: '700', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Academic Services
          </span>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '800', color: '#1e1b4b', letterSpacing: '-0.5px' }}>
            Library Management
          </h1>
          <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '13px', fontWeight: '500' }}>
            Manage book titles, physical copies, borrowing transactions, returns, and overdue fines.
          </p>
        </div>

        {/* QUICK ACTION BUTTONS */}
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => setActiveTab('register')}
            style={{
              padding: '10px 16px', background: '#4F46E5', color: '#fff', border: 'none',
              borderRadius: '10px', fontWeight: '700', fontSize: '12.5px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 4px 12px rgba(79, 70, 229, 0.25)'
            }}
          >
            <Book size={16} /> + Add Book
          </button>
          <button
            onClick={() => { setActiveTab('issue'); setIssueStep(1); }}
            style={{
              padding: '10px 16px', background: '#0284C7', color: '#fff', border: 'none',
              borderRadius: '10px', fontWeight: '700', fontSize: '12.5px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 4px 12px rgba(2, 132, 199, 0.25)'
            }}
          >
            <BookOpen size={16} /> Issue Book
          </button>
          <button
            onClick={() => setActiveTab('return')}
            style={{
              padding: '10px 16px', background: '#16A34A', color: '#fff', border: 'none',
              borderRadius: '10px', fontWeight: '700', fontSize: '12.5px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 4px 12px rgba(22, 163, 74, 0.25)'
            }}
          >
            <CheckCircle size={16} /> Return Book
          </button>
        </div>
      </div>

      {/* WORKFLOW SUB-NAVIGATION TABS */}
      <div style={{
        display: 'flex', gap: '8px', backgroundColor: '#ffffff', padding: '6px',
        borderRadius: '12px', boxShadow: '0 2px 6px rgba(0,0,0,0.04)', marginBottom: '20px'
      }}>
        {[
          { id: 'inventory', label: 'Inventory / Dashboard', icon: Layers },
          { id: 'register', label: 'Register Book', icon: Book },
          { id: 'issue', label: 'Issue Book', icon: BookOpen },
          { id: 'return', label: 'Return Book', icon: CheckCircle },
          { id: 'transactions', label: 'Transactions History', icon: Clock },
          { id: 'overdue', label: `Overdue & Fines (${overdueList.length})`, icon: AlertTriangle }
        ].map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1, padding: '10px 14px', borderRadius: '8px', border: 'none',
                fontWeight: isActive ? '800' : '600', fontSize: '12.5px', cursor: 'pointer',
                backgroundColor: isActive ? '#4F46E5' : 'transparent',
                color: isActive ? '#ffffff' : '#64748b',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                transition: 'all 0.15s ease'
              }}
            >
              <Icon size={16} /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* KPI DASHBOARD CARDS (ONLY ON INVENTORY DASHBOARD) */}
      {activeTab === 'inventory' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px', marginBottom: '24px' }}>
          {[
            { title: 'TOTAL TITLES', value: libraryStats.totalBooks, icon: '📖', color: '#6366f1', bg: 'rgba(99,102,241,0.1)', sub: 'Distinct Titles' },
            { title: 'TOTAL COPIES', value: libraryStats.totalCopies, icon: '📄', color: '#f97316', bg: 'rgba(249,115,22,0.1)', sub: 'Physical Copies' },
            { title: 'AVAILABLE', value: libraryStats.availableCopies, icon: '📝', color: '#22c55e', bg: 'rgba(34,197,94,0.1)', sub: `${Math.round((libraryStats.availableCopies / (libraryStats.totalCopies || 1)) * 100)}% Available` },
            { title: 'ISSUED BOOKS', value: libraryStats.issuedCopies, icon: '📤', color: '#3b82f6', bg: 'rgba(59,130,246,0.1)', sub: 'Active Loans' },
            { title: 'OVERDUE BOOKS', value: libraryStats.overdueCopies, icon: '⏱️', color: '#ef4444', bg: 'rgba(239,68,68,0.1)', sub: 'Requires Action' }
          ].map((card, idx) => (
            <div key={idx} style={{
              padding: '16px', borderBottom: `3px solid ${card.color}`, borderRadius: '12px',
              backgroundColor: '#ffffff', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: card.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>
                  {card.icon}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: '10px', fontWeight: '800', color: '#64748b', letterSpacing: '0.5px' }}>{card.title}</span>
                  <div style={{ fontSize: '22px', fontWeight: '800', color: '#1e1b4b', marginTop: '2px' }}>{card.value}</div>
                </div>
              </div>
              <div style={{ fontSize: '10.5px', color: '#94a3b8', fontWeight: '600', marginTop: '10px', textAlign: 'center' }}>
                {card.sub}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ==================== TAB 1: INVENTORY ==================== */}
      {activeTab === 'inventory' && (
        <div style={{ backgroundColor: '#ffffff', padding: '24px', borderRadius: '14px', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          {/* SEARCH & FILTERS */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <Search size={16} style={{ position: 'absolute', left: '14px', top: '12px', color: '#94a3b8' }} />
              <input
                type="text"
                placeholder="Search by Title, Author, ISBN..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%', padding: '10px 14px 10px 38px', borderRadius: '10px',
                  border: '1px solid #E2E8F0', fontSize: '13px', boxSizing: 'border-box'
                }}
              />
            </div>

            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              style={{ padding: '10px 14px', borderRadius: '10px', border: '1px solid #E2E8F0', fontSize: '13px', background: 'white' }}
            >
              <option value="">All Categories / Subjects</option>
              {Array.from(new Set(libraryBooks.map(b => b.subject).filter(Boolean))).map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* INVENTORY TABLE */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#F8FAFC', borderBottom: '2px solid #E2E8F0' }}>
                  <th style={{ padding: '12px 14px', color: '#64748B', fontWeight: '700' }}>ID</th>
                  <th style={{ padding: '12px 14px', color: '#0F172A', fontWeight: '700' }}>Book Title</th>
                  <th style={{ padding: '12px 14px', color: '#475569', fontWeight: '700' }}>Author</th>
                  <th style={{ padding: '12px 14px', color: '#475569', fontWeight: '700' }}>Category</th>
                  <th style={{ padding: '12px 14px', color: '#475569', fontWeight: '700' }}>ISBN</th>
                  <th style={{ padding: '12px 14px', color: '#475569', fontWeight: '700' }}>Copies (Avail / Total)</th>
                  <th style={{ padding: '12px 14px', color: '#475569', fontWeight: '700' }}>Rack</th>
                  <th style={{ padding: '12px 14px', color: '#475569', fontWeight: '700', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBooks.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ padding: '32px', textAlign: 'center', color: '#64748B' }}>
                      No book titles in library inventory. Click <strong>+ Add Book</strong> to register titles and physical copies.
                    </td>
                  </tr>
                ) : (
                  filteredBooks.map(book => (
                    <tr key={book.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                      <td style={{ padding: '12px 14px', fontWeight: '700', color: '#4F46E5' }}>#{book.id}</td>
                      <td style={{ padding: '12px 14px', fontWeight: '700', color: '#0F172A' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Book size={16} color="#4F46E5" />
                          <span>{book.title}</span>
                        </div>
                      </td>
                      <td style={{ padding: '12px 14px', color: '#334155' }}>{book.author}</td>
                      <td style={{ padding: '12px 14px' }}>
                        <span style={{ padding: '4px 8px', borderRadius: '6px', background: 'rgba(99,102,241,0.1)', color: '#4F46E5', fontWeight: '700', fontSize: '11px' }}>
                          {book.subject || 'General'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', color: '#64748B', fontFamily: 'monospace' }}>{book.isbn || '-'}</td>
                      <td style={{ padding: '12px 14px' }}>
                        <span style={{ padding: '4px 8px', borderRadius: '6px', background: book.availableCopies > 0 ? '#DCFCE7' : '#FEE2E2', color: book.availableCopies > 0 ? '#15803D' : '#B91C1C', fontWeight: '700', fontSize: '11.5px' }}>
                          {book.availableCopies} Available
                        </span>
                        <span style={{ color: '#64748B', fontSize: '11px', marginLeft: '6px' }}>/ {book.totalCopies} Total</span>
                      </td>
                      <td style={{ padding: '12px 14px', color: '#475569' }}>{book.rackLocation || 'Unassigned'}</td>
                      <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                        <button
                          onClick={() => handleInspectCopies(book)}
                          style={{ padding: '6px 12px', background: '#F1F5F9', color: '#475569', border: 'none', borderRadius: '6px', fontWeight: '700', fontSize: '11.5px', cursor: 'pointer' }}
                        >
                          View Physical Copies
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ==================== TAB 2: REGISTER BOOK ==================== */}
      {activeTab === 'register' && (
        <div style={{ backgroundColor: '#ffffff', padding: '28px', borderRadius: '14px', boxShadow: '0 4px 12px rgba(0,0,0,0.03)', maxWidth: '800px', margin: '0 auto' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '800', color: '#0F172A' }}>
            📖 Register New Book Title & Generate Physical Copies
          </h3>
          <p style={{ margin: '0 0 24px 0', fontSize: '13px', color: '#64748B' }}>
            Registering a book automatically generates unique physical Accession IDs (e.g. <code>LIB-2026-0001</code>) for each copy.
          </p>

          <form onSubmit={handleSaveBook} style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>Book Title *</label>
              <input
                type="text" required
                placeholder="e.g. Introduction to Algorithms"
                value={bookForm.title}
                onChange={(e) => setBookForm({ ...bookForm, title: e.target.value })}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #E2E8F0', boxSizing: 'border-box' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>Author Name *</label>
              <input
                type="text" required
                placeholder="e.g. Thomas H. Cormen"
                value={bookForm.author}
                onChange={(e) => setBookForm({ ...bookForm, author: e.target.value })}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #E2E8F0', boxSizing: 'border-box' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>Category / Subject *</label>
              <input
                type="text" required
                placeholder="e.g. Computer Science"
                value={bookForm.subject}
                onChange={(e) => setBookForm({ ...bookForm, subject: e.target.value })}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #E2E8F0', boxSizing: 'border-box' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>ISBN Number</label>
              <input
                type="text"
                placeholder="e.g. 978-0262033848"
                value={bookForm.isbn}
                onChange={(e) => setBookForm({ ...bookForm, isbn: e.target.value })}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #E2E8F0', boxSizing: 'border-box' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>Publisher</label>
              <input
                type="text"
                placeholder="e.g. MIT Press"
                value={bookForm.publisher}
                onChange={(e) => setBookForm({ ...bookForm, publisher: e.target.value })}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #E2E8F0', boxSizing: 'border-box' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>Number of Physical Copies *</label>
              <input
                type="number" min="1" max="100" required
                value={bookForm.totalCopies}
                onChange={(e) => setBookForm({ ...bookForm, totalCopies: e.target.value })}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #E2E8F0', boxSizing: 'border-box' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>Rack / Shelf Location</label>
              <input
                type="text"
                placeholder="e.g. Rack B2 - Shelf 4"
                value={bookForm.rackLocation}
                onChange={(e) => setBookForm({ ...bookForm, rackLocation: e.target.value })}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #E2E8F0', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ gridColumn: 'span 2', display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
              <button
                type="button" onClick={() => setActiveTab('inventory')}
                style={{ padding: '10px 20px', background: '#F1F5F9', color: '#64748B', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{ padding: '10px 24px', background: '#4F46E5', color: '#ffffff', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer' }}
              >
                Save & Generate Physical Copies
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ==================== TAB 3: ISSUE BOOK (FAST 2-STEP WORKFLOW) ==================== */}
      {activeTab === 'issue' && (
        <div style={{ backgroundColor: '#ffffff', padding: '28px', borderRadius: '14px', boxShadow: '0 4px 12px rgba(0,0,0,0.03)', maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0F172A' }}>
              📤 Librarian Fast Issue Scanner
            </h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <span style={{ padding: '4px 12px', borderRadius: '20px', background: issueStep === 1 ? '#4F46E5' : '#E2E8F0', color: issueStep === 1 ? '#fff' : '#64748B', fontSize: '12px', fontWeight: '700' }}>
                Step 1: Select Borrower
              </span>
              <span style={{ padding: '4px 12px', borderRadius: '20px', background: issueStep === 2 ? '#4F46E5' : '#E2E8F0', color: issueStep === 2 ? '#fff' : '#64748B', fontSize: '12px', fontWeight: '700' }}>
                Step 2: Scan Book Accession
              </span>
            </div>
          </div>

          {/* STEP 1: BORROWER SELECT */}
          {issueStep === 1 && (
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#334155', marginBottom: '8px' }}>
                Scan or Enter Borrower ID / Student ID / Roll No / Name:
              </label>
              <input
                type="text"
                autoFocus
                placeholder="e.g. SCH-2026-0030 or Student / Teacher Name..."
                value={issueBorrowerQuery}
                onChange={(e) => setIssueBorrowerQuery(e.target.value)}
                style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '2px solid #4F46E5', fontSize: '14px', boxSizing: 'border-box', marginBottom: '16px' }}
              />

              {matchingBorrowers().length > 0 && (
                <div style={{ border: '1px solid #E2E8F0', borderRadius: '10px', overflow: 'hidden', marginBottom: '20px' }}>
                  {matchingBorrowers().map(b => (
                    <div
                      key={b.id}
                      onClick={() => { setSelectedBorrower(b); setIssueStep(2); }}
                      style={{
                        padding: '12px 16px', borderBottom: '1px solid #F1F5F9', cursor: 'pointer',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        backgroundColor: selectedBorrower?.id === b.id ? '#EFF6FF' : 'white'
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: '700', color: '#0F172A' }}>{b.name}</div>
                        <div style={{ fontSize: '11.5px', color: '#64748B' }}>ID: <code>{b.id}</code> | {b.type} ({b.class})</div>
                      </div>
                      <ArrowRight size={16} color="#4F46E5" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* STEP 2: SCAN BOOK ACCESSION */}
          {issueStep === 2 && selectedBorrower && (
            <div>
              <div style={{ padding: '12px 16px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '10px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ fontSize: '11px', fontWeight: '800', color: '#166534', textTransform: 'uppercase' }}>Selected Borrower</span>
                  <div style={{ fontWeight: '800', color: '#14532D', fontSize: '15px' }}>{selectedBorrower.name} (ID: {selectedBorrower.id})</div>
                  <div style={{ fontSize: '12px', color: '#15803D' }}>{selectedBorrower.type} • Class: {selectedBorrower.class}</div>
                </div>
                <button onClick={() => setIssueStep(1)} style={{ padding: '4px 10px', background: '#DCFCE7', color: '#15803D', border: 'none', borderRadius: '6px', fontWeight: '700', cursor: 'pointer' }}>
                  Change
                </button>
              </div>

              <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#334155', marginBottom: '8px' }}>
                Scan / Enter Physical Book Accession Number (e.g. <code>LIB-2026-0001</code>):
              </label>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                <input
                  type="text"
                  autoFocus
                  placeholder="Scan or enter Accession ID..."
                  value={issueAccessionQuery}
                  onChange={(e) => setIssueAccessionQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleLookupIssueCopy(issueAccessionQuery); }}
                  style={{ flex: 1, padding: '12px 16px', borderRadius: '10px', border: '2px solid #0284C7', fontSize: '14px' }}
                />
                <button
                  type="button"
                  onClick={() => handleLookupIssueCopy(issueAccessionQuery)}
                  style={{ padding: '12px 20px', background: '#0284C7', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: '700', cursor: 'pointer' }}
                >
                  Lookup Copy
                </button>
              </div>

              {selectedCopyData && (
                <div style={{ padding: '16px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '10px', marginBottom: '24px' }}>
                  <div style={{ fontWeight: '800', fontSize: '16px', color: '#0F172A' }}>{selectedCopyData.book?.title}</div>
                  <div style={{ fontSize: '12.5px', color: '#64748B', margin: '4px 0 12px 0' }}>Author: {selectedCopyData.book?.author} | Subject: {selectedCopyData.book?.subject}</div>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <span style={{ padding: '4px 10px', borderRadius: '6px', background: selectedCopyData.copy?.status === 'AVAILABLE' ? '#DCFCE7' : '#FEE2E2', color: selectedCopyData.copy?.status === 'AVAILABLE' ? '#15803D' : '#B91C1C', fontWeight: '800', fontSize: '12px' }}>
                      Status: {selectedCopyData.copy?.status}
                    </span>
                    <span style={{ fontSize: '12px', fontWeight: '700', color: '#475569' }}>
                      Accession: <code>{selectedCopyData.copy?.accessionNo}</code>
                    </span>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button onClick={() => setIssueStep(1)} style={{ padding: '10px 18px', background: '#F1F5F9', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer' }}>Back</button>
                <button
                  disabled={!selectedCopyData || selectedCopyData.copy?.status !== 'AVAILABLE' || issueSubmitting}
                  onClick={handleConfirmIssue}
                  style={{
                    padding: '10px 24px', background: selectedCopyData?.copy?.status === 'AVAILABLE' ? '#16A34A' : '#94A3B8',
                    color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: selectedCopyData?.copy?.status === 'AVAILABLE' ? 'pointer' : 'not-allowed'
                  }}
                >
                  {issueSubmitting ? 'Issuing...' : 'Confirm & Issue Book'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ==================== TAB 4: RETURN BOOK (FAST SCAN WORKFLOW) ==================== */}
      {activeTab === 'return' && (
        <div style={{ backgroundColor: '#ffffff', padding: '28px', borderRadius: '14px', boxShadow: '0 4px 12px rgba(0,0,0,0.03)', maxWidth: '800px', margin: '0 auto' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '800', color: '#0F172A' }}>
            📥 Librarian Fast Return Scanner
          </h3>

          <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#334155', marginBottom: '8px' }}>
            Scan / Enter Book Accession Number (e.g. <code>LIB-2026-0001</code>):
          </label>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '24px' }}>
            <input
              type="text"
              autoFocus
              placeholder="Scan Accession ID..."
              value={returnAccessionQuery}
              onChange={(e) => setReturnAccessionQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleLookupReturnCopy(returnAccessionQuery); }}
              style={{ flex: 1, padding: '12px 16px', borderRadius: '10px', border: '2px solid #16A34A', fontSize: '14px' }}
            />
            <button
              onClick={() => handleLookupReturnCopy(returnAccessionQuery)}
              style={{ padding: '12px 20px', background: '#16A34A', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: '700', cursor: 'pointer' }}
            >
              Scan & Find Loan
            </button>
          </div>

          {activeReturnCopy && activeReturnCopy.activeIssue && (
            <div style={{ padding: '20px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '12px', marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <div style={{ fontSize: '18px', fontWeight: '800', color: '#0F172A' }}>{activeReturnCopy.activeIssue.bookTitle}</div>
                  <div style={{ fontSize: '13px', color: '#64748B', marginTop: '2px' }}>
                    Borrower: <strong>{activeReturnCopy.activeIssue.borrowerName}</strong> ({activeReturnCopy.activeIssue.borrowerType} #{activeReturnCopy.activeIssue.borrowerId})
                  </div>
                </div>
                <span style={{ padding: '4px 10px', borderRadius: '6px', background: '#EFF6FF', color: '#2563EB', fontWeight: '700', fontSize: '12px' }}>
                  Loan Active
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', padding: '12px', background: 'white', borderRadius: '8px', border: '1px solid #E2E8F0', marginBottom: '16px' }}>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748B', fontWeight: '700' }}>ISSUE DATE</div>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#0F172A' }}>{activeReturnCopy.activeIssue.issueDate}</div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748B', fontWeight: '700' }}>DUE DATE</div>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#2563EB' }}>{activeReturnCopy.activeIssue.dueDate}</div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748B', fontWeight: '700' }}>FINE ASSESSED</div>
                  <div style={{ fontSize: '13px', fontWeight: '800', color: activeReturnCopy.activeIssue.fineAmount > 0 ? '#DC2626' : '#16A34A' }}>
                    ₹{activeReturnCopy.activeIssue.fineAmount || 0}
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>Returned Book Condition:</label>
                <select
                  value={returnCondition}
                  onChange={(e) => setReturnCondition(e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', fontWeight: '700' }}
                >
                  <option value="GOOD">Good Condition (Return to Shelf)</option>
                  <option value="DAMAGED">Damaged (Mark for Repair)</option>
                  <option value="LOST">Lost by Borrower</option>
                </select>
              </div>

              <button
                disabled={returnSubmitting}
                onClick={handleConfirmReturn}
                style={{ width: '100%', padding: '12px', background: '#16A34A', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: '800', fontSize: '14px', cursor: 'pointer' }}
              >
                {returnSubmitting ? 'Processing...' : 'Confirm Book Return'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ==================== TAB 5: TRANSACTIONS HISTORY ==================== */}
      {activeTab === 'transactions' && (
        <div style={{ backgroundColor: '#ffffff', padding: '24px', borderRadius: '14px', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0F172A' }}>📜 Transaction Audit Register</h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              {['ALL', 'ISSUED', 'RETURNED', 'OVERDUE'].map(st => (
                <button
                  key={st}
                  onClick={() => setTransactionFilter(st)}
                  style={{
                    padding: '6px 12px', borderRadius: '6px', border: 'none', fontWeight: '700', fontSize: '12px', cursor: 'pointer',
                    backgroundColor: transactionFilter === st ? '#4F46E5' : '#F1F5F9',
                    color: transactionFilter === st ? '#FFF' : '#64748B'
                  }}
                >
                  {st}
                </button>
              ))}
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#F8FAFC', borderBottom: '2px solid #E2E8F0' }}>
                  <th style={{ padding: '12px 14px', color: '#64748B', fontWeight: '700' }}>TX ID</th>
                  <th style={{ padding: '12px 14px', color: '#0F172A', fontWeight: '700' }}>Book Title</th>
                  <th style={{ padding: '12px 14px', color: '#475569', fontWeight: '700' }}>Accession ID</th>
                  <th style={{ padding: '12px 14px', color: '#475569', fontWeight: '700' }}>Borrower</th>
                  <th style={{ padding: '12px 14px', color: '#475569', fontWeight: '700' }}>Issue Date</th>
                  <th style={{ padding: '12px 14px', color: '#475569', fontWeight: '700' }}>Due Date</th>
                  <th style={{ padding: '12px 14px', color: '#475569', fontWeight: '700' }}>Status</th>
                  <th style={{ padding: '12px 14px', color: '#475569', fontWeight: '700' }}>Fine</th>
                  <th style={{ padding: '12px 14px', color: '#475569', fontWeight: '700', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.length === 0 ? (
                  <tr><td colSpan="9" style={{ padding: '32px', textAlign: 'center', color: '#64748B' }}>No borrowing transactions recorded.</td></tr>
                ) : (
                  filteredTransactions.map(tx => (
                    <tr key={tx.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                      <td style={{ padding: '12px 14px', fontWeight: '700', color: '#4F46E5' }}>#{tx.id}</td>
                      <td style={{ padding: '12px 14px', fontWeight: '700', color: '#0F172A' }}>{tx.bookTitle}</td>
                      <td style={{ padding: '12px 14px', fontFamily: 'monospace', fontWeight: '700' }}>{tx.accessionNo}</td>
                      <td style={{ padding: '12px 14px' }}>{tx.borrowerName} ({tx.borrowerType})</td>
                      <td style={{ padding: '12px 14px' }}>{tx.issueDate}</td>
                      <td style={{ padding: '12px 14px', color: '#2563EB', fontWeight: '700' }}>{tx.dueDate}</td>
                      <td style={{ padding: '12px 14px' }}>
                        <span style={{
                          padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '800',
                          background: tx.status === 'RETURNED' ? '#DCFCE7' : '#EFF6FF',
                          color: tx.status === 'RETURNED' ? '#15803D' : '#2563EB'
                        }}>
                          {tx.status}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', fontWeight: '700', color: tx.fineAmount > 0 ? '#DC2626' : '#16A34A' }}>
                        ₹{tx.fineAmount || 0}
                      </td>
                      <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                        {tx.status === 'ISSUED' && (
                          <button
                            onClick={() => handleRenewBook(tx.id)}
                            style={{ padding: '4px 10px', background: '#EFF6FF', color: '#2563EB', border: 'none', borderRadius: '6px', fontWeight: '700', fontSize: '11px', cursor: 'pointer' }}
                          >
                            Renew
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ==================== TAB 6: OVERDUE & FINES ==================== */}
      {activeTab === 'overdue' && (
        <div style={{ backgroundColor: '#ffffff', padding: '24px', borderRadius: '14px', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '800', color: '#DC2626' }}>
            ⚠️ Active Overdue Loans & Fine Collection Register
          </h3>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#FEF2F2', borderBottom: '2px solid #FCA5A5' }}>
                  <th style={{ padding: '12px 14px', color: '#991B1B', fontWeight: '700' }}>Borrower Name</th>
                  <th style={{ padding: '12px 14px', color: '#991B1B', fontWeight: '700' }}>Book Title</th>
                  <th style={{ padding: '12px 14px', color: '#991B1B', fontWeight: '700' }}>Accession ID</th>
                  <th style={{ padding: '12px 14px', color: '#991B1B', fontWeight: '700' }}>Due Date</th>
                  <th style={{ padding: '12px 14px', color: '#991B1B', fontWeight: '700' }}>Overdue Days</th>
                  <th style={{ padding: '12px 14px', color: '#991B1B', fontWeight: '700' }}>Fine Assessed</th>
                  <th style={{ padding: '12px 14px', color: '#991B1B', fontWeight: '700' }}>Status</th>
                  <th style={{ padding: '12px 14px', color: '#991B1B', fontWeight: '700', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {overdueList.length === 0 ? (
                  <tr><td colSpan="8" style={{ padding: '32px', textAlign: 'center', color: '#16A34A', fontWeight: '700' }}>🎉 No overdue books in library register!</td></tr>
                ) : (
                  overdueList.map(tx => (
                    <tr key={tx.id} style={{ borderBottom: '1px solid #FEE2E2' }}>
                      <td style={{ padding: '12px 14px', fontWeight: '700', color: '#0F172A' }}>{tx.borrowerName} ({tx.borrowerType})</td>
                      <td style={{ padding: '12px 14px', fontWeight: '700' }}>{tx.bookTitle}</td>
                      <td style={{ padding: '12px 14px', fontFamily: 'monospace' }}>{tx.accessionNo}</td>
                      <td style={{ padding: '12px 14px', color: '#DC2626', fontWeight: '700' }}>{tx.dueDate}</td>
                      <td style={{ padding: '12px 14px', fontWeight: '800', color: '#DC2626' }}>{tx.calendarOverdueDays || 0} Days</td>
                      <td style={{ padding: '12px 14px', fontWeight: '800', color: '#B91C1C' }}>₹{tx.fineAmount || 0}</td>
                      <td style={{ padding: '12px 14px' }}>
                        <span style={{ padding: '4px 8px', borderRadius: '6px', background: '#FEE2E2', color: '#DC2626', fontWeight: '800', fontSize: '11px' }}>
                          {tx.fineStatus || 'UNPAID'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                        <button
                          onClick={() => { setPayingFineIssue(tx); setFinePayAmount(String(tx.fineAmount || 0)); }}
                          style={{ padding: '6px 12px', background: '#16A34A', color: '#FFF', border: 'none', borderRadius: '6px', fontWeight: '700', fontSize: '11.5px', cursor: 'pointer' }}
                        >
                          Collect Fine
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* PHYSICAL COPIES INSPECTION MODAL */}
      {selectedBookForCopies && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#FFF', borderRadius: '16px', padding: '28px', width: '640px', maxWidth: '90vw', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0F172A' }}>{selectedBookForCopies.title}</h3>
                <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#64748B' }}>Physical Copy Accession Register</p>
              </div>
              <button onClick={() => setSelectedBookForCopies(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>

            {loadingCopies ? (
              <div style={{ padding: '32px', textAlign: 'center', color: '#64748B' }}>Loading physical copies...</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#F8FAFC', borderBottom: '2px solid #E2E8F0' }}>
                    <th style={{ padding: '10px', color: '#64748B' }}>Accession No</th>
                    <th style={{ padding: '10px', color: '#64748B' }}>Barcode</th>
                    <th style={{ padding: '10px', color: '#64748B' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bookCopies.map(copy => (
                    <tr key={copy.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                      <td style={{ padding: '10px', fontWeight: '700', fontFamily: 'monospace', color: '#4F46E5' }}>{copy.accessionNo}</td>
                      <td style={{ padding: '10px', fontFamily: 'monospace', color: '#64748B' }}>{copy.barcode || copy.accessionNo}</td>
                      <td style={{ padding: '10px' }}>
                        <span style={{
                          padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '800',
                          background: copy.status === 'AVAILABLE' ? '#DCFCE7' : '#FEE2E2',
                          color: copy.status === 'AVAILABLE' ? '#15803D' : '#B91C1C'
                        }}>
                          {copy.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* FINE PAYMENT MODAL */}
      {payingFineIssue && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#FFF', borderRadius: '16px', padding: '24px', width: '400px' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '800' }}>Collect Overdue Fine</h3>
            <p style={{ fontSize: '13px', color: '#64748B', margin: '0 0 16px 0' }}>
              Borrower: <strong>{payingFineIssue.borrowerName}</strong><br />
              Assessed Fine: <strong>₹{payingFineIssue.fineAmount}</strong>
            </p>
            <form onSubmit={handlePayFineSubmit}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', marginBottom: '6px' }}>Payment Amount (₹):</label>
              <input
                type="number" step="1" required
                value={finePayAmount}
                onChange={(e) => setFinePayAmount(e.target.value)}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #E2E8F0', boxSizing: 'border-box', marginBottom: '16px' }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button type="button" onClick={() => setPayingFineIssue(null)} style={{ padding: '8px 16px', background: '#F1F5F9', border: 'none', borderRadius: '8px' }}>Cancel</button>
                <button type="submit" style={{ padding: '8px 20px', background: '#16A34A', color: '#FFF', border: 'none', borderRadius: '8px', fontWeight: '700' }}>Confirm Payment</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default LibraryManagement;
